/**
 * Sessão × relógio do PC — GUPM-336.
 *
 * O auth-js decide se o access token venceu comparando `expires_at` (hora do servidor)
 * com `Date.now()` (hora do PC). Numa estação cujo relógio reseta ao reiniciar isso
 * falha calado: com o PC atrasado, o client acha o token válido, nunca pede `/token`, e
 * o PostgREST recusa tudo com `PGRST303` (29/09/2026: 1h09 de painel travado).
 *
 * As regras aqui não confiam no relógio do PC para nada que importe: o vencimento vem
 * do SERVIDOR (o 401) e o desvio é medido contra o `iat` que o servidor carimbou.
 *
 * TS puro (regra #1): `atob` é global do JS, não do DOM.
 */

/** Acima disto o desvio não é latência de rede nem arredondamento do `iat` (1 s). */
export const LIMIAR_DESVIO_MS = 2 * 60_000;

/** Folga para renovar antes do vencimento real. */
const FOLGA_RENOVACAO_SEG = 300;

/** Piso do agendamento — nunca renovar em laço. */
const ATRASO_MINIMO_MS = 10_000;

/** `401` + `PGRST303` é o PostgREST dizendo que o JWT venceu. Só isso. */
export function ehJwtExpirado(status: number, corpo: unknown): boolean {
  if (status !== 401 || typeof corpo !== 'object' || corpo === null) return false;
  return (corpo as { code?: unknown }).code === 'PGRST303';
}

/** `iat` (segundos, hora do servidor) do payload do JWT; `null` se ilegível. */
export function iatDoToken(accessToken: string): number | null {
  const payload = accessToken.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const iat = (JSON.parse(json) as { iat?: unknown }).iat;
    return typeof iat === 'number' && Number.isFinite(iat) ? iat : null;
  } catch {
    return null;
  }
}

/**
 * Desvio do relógio do PC em ms: positivo = adiantado, negativo = atrasado.
 * Só vale para token RECÉM-emitido — `agoraMs` é o instante em que ele chegou.
 */
export function desvioDoRelogioMs(iatSeg: number, agoraMs: number): number {
  return agoraMs - iatSeg * 1000;
}

export function relogioDesregulado(desvioMs: number | null): boolean {
  return desvioMs !== null && Math.abs(desvioMs) > LIMIAR_DESVIO_MS;
}

/** "1h20 atrasado", "5 min adiantado", "2h atrasado". */
export function descreverDesvio(desvioMs: number): string {
  const totalMin = Math.round(Math.abs(desvioMs) / 60_000);
  const h = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  const quanto = h === 0 ? `${min} min` : min === 0 ? `${h}h` : `${h}h${String(min).padStart(2, '0')}`;
  return `${quanto} ${desvioMs < 0 ? 'atrasado' : 'adiantado'}`;
}

/**
 * Daqui a quanto renovar um token que vale `expiresInSeg` a partir de AGORA.
 * Tempo relativo: independe da hora do PC.
 */
export function atrasoDaRenovacaoMs(expiresInSeg: number): number {
  const seg = expiresInSeg > 2 * FOLGA_RENOVACAO_SEG ? expiresInSeg - FOLGA_RENOVACAO_SEG : expiresInSeg / 2;
  return Math.max(seg * 1000, ATRASO_MINIMO_MS);
}

/**
 * Orientação ao usuário para uma falha de carga, em linguagem de operação.
 * Nunca instrução de infraestrutura — essa é do desenvolvedor (ver `FalhaDeCarga`).
 */
export function orientacaoDaFalhaDeCarga(mensagem: string): string {
  if (/jwt expired|PGRST303/i.test(mensagem)) {
    return 'A sessão está sendo renovada automaticamente. Se a mensagem continuar, saia e entre de novo.';
  }
  if (/failed to fetch|network|fetch failed|load failed/i.test(mensagem)) {
    return 'Sem conexão com o servidor. Verifique a internet deste computador — o app tenta de novo sozinho.';
  }
  // O contato do suporte vem na linha seguinte do cartão (`FalhaDeCarga`).
  return 'O app tenta de novo automaticamente.';
}
